/**
 * PushToast — transient notification shown when a push arrives while the app
 * is in the foreground.  Auto-dismisses after 4 seconds.
 *
 * Listens for the `push:toast` custom event dispatched by usePushNotifications.
 */

import { useState, useEffect } from 'react'

interface ToastData { title: string; body: string }

export default function PushToast() {
  const [toast, setToast] = useState<ToastData | null>(null)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ToastData>).detail
      setToast(detail)
      clearTimeout(timer)
      timer = setTimeout(() => setToast(null), 4000)
    }

    window.addEventListener('push:toast', handler)
    return () => {
      window.removeEventListener('push:toast', handler)
      clearTimeout(timer)
    }
  }, [])

  if (!toast) return null

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 10000,
        background: 'var(--bg-card, #fff)',
        border: '1px solid var(--border-color, #e5e7eb)',
        borderRadius: 10,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        padding: '12px 16px',
        maxWidth: 320,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        cursor: 'pointer',
      }}
      onClick={() => setToast(null)}
    >
      <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>
        {toast.title}
      </span>
      {toast.body && (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {toast.body}
        </span>
      )}
    </div>
  )
}
